// Centralized content for Bronto Intelligence Private Limited

export const COMPANY = {
  legalName: "Bronto Platforms Private Limited",
  displayName: "Bronto Platforms",
  shortName: "Bronto Platforms",
  parentName: "Bronto Intelligence Private Limited",
  parentUrl: "https://brontointelligence.com",
  tagline: "Building the Platforms Behind the Bronto Ecosystem.",
  description:
    "Bronto Platforms Private Limited is the product and platform subsidiary of Bronto Intelligence Private Limited, responsible for the development, operation, management, and commercialization of the ecosystem's digital platforms — including BrontoAI, BrontoY, MSPConnect, and Bronto Cosmos.",
  longDescription:
    "As a subsidiary of Bronto Intelligence Private Limited, Bronto Platforms turns strategic vision into world-class digital products. The company builds, operates, and scales the platforms that define the Bronto ecosystem — combining artificial intelligence, agricultural intelligence, space discovery, digital infrastructure, and public-impact technology to build practical systems that improve access to information, decision-making, and technology-enabled services.",
  mission:
    "To build and operate intelligent digital platforms that improve how people interact with technology, information, and markets.",
  vision:
    "To develop and scale a long-term portfolio of digital platforms spanning artificial intelligence, social networking, economic intelligence, agricultural technology, and future digital infrastructure.",
  logoUrl:
    "/favicon-192.png",
  founded: "India",
};

export const CORPORATE_STRUCTURE = [
  {
    name: "Bronto Intelligence Private Limited",
    role: "Parent Company",
    description:
      "Provides strategic leadership, governance, and long-term direction across the entire Bronto ecosystem.",
    level: 0,
  },
  {
    name: "Bronto Platforms Private Limited",
    role: "Product & Platform Subsidiary",
    description:
      "Subsidiary responsible for the development, operation, management, and commercialization of the company's digital platforms.",
    level: 1,
  },
  {
    name: "BrontoAI",
    role: "Human-Centric Artificial Intelligence Platform",
    description:
      "Consumer-facing artificial intelligence platform focused on human-centered interaction and accessible AI experiences.",
    level: 2,
  },
  {
    name: "BrontoY",
    role: "Social Networking Platform",
    description:
      "Social networking platform for sharing moments, expressing opinions, and discovering different perspectives.",
    level: 2,
  },
  {
    name: "MSPConnect",
    role: "Agricultural Intelligence Platform",
    description:
      "Agricultural intelligence and market-awareness platform improving transparency and access to information.",
    level: 2,
  },
  {
    name: "Bronto Cosmos Private Limited",
    role: "Space & Science Subsidiary",
    description:
      "Subsidiary responsible for the development and operation of the company's space discovery and astronomy platforms.",
    level: 1,
  },
  {
    name: "Bronto Cosmos",
    role: "Space Discovery & Astronomy Platform",
    description:
      "Science, astronomy, and space discovery platform that brings real cosmic data into a single, beautifully designed discovery experience.",
    level: 2,
  },
];

export const PLATFORMS = [
  {
    id: "brontoai",
    name: "BrontoAI",
    category: "Human-Centric Artificial Intelligence",
    shortDescription:
      "A human-centric artificial intelligence platform for natural conversation, multimodal interaction, and everyday AI assistance.",
    description:
      "BrontoAI is a human-centric artificial intelligence platform designed to provide natural conversation, multimodal interaction, image generation, voice experiences, productivity assistance, and everyday AI support. The platform focuses on intuitive interaction, contextual understanding, and accessible artificial intelligence experiences.",
    capabilities: [
      "Natural Conversation",
      "Multimodal Interaction",
      "Image Generation",
      "Voice Experiences",
      "Productivity Assistance",
      "Contextual Understanding",
    ],
    appStoreUrl: "https://apps.apple.com/app/id6757022183",
    buttonText: "Download BrontoAI on the App Store",
    gradient: "from-blue-500 via-purple-500 to-pink-500",
    accentColor: "#a855f7",
  },
  {
    id: "brontoy",
    name: "BrontoY",
    category: "Social Networking",
    shortDescription:
      "A thoughtfully designed social platform for sharing moments, expressing opinions, and discovering different perspectives.",
    description:
      "BrontoY is a warm social space for sharing moments, expressing opinions, and discovering different views — designed to feel simple, thoughtful, and natural. Standard posts let people share photos and captions from everyday moments, while Opinion Posts offer a dedicated space to express what one thinks. Others can agree with an opinion or share a Different View — capped at 50 words to keep conversations focused, clear, and easy to explore. BrontoY brings together moments and opinions in one social space, built for sharing, discovering, and connecting.",
    capabilities: [
      "Share Moments",
      "Opinion Posts",
      "Different Views",
      "Follow & Connect",
      "Likes & Comments",
      "Personal Profile",
    ],
    appStoreUrl: "https://apps.apple.com/in/app/brontoy/id6788015223",
    buttonText: "Download BrontoY on the App Store",
    gradient: "from-orange-400 via-pink-500 to-rose-500",
    accentColor: "#f43f5e",
  },
  {
    id: "brontocosmos",
    name: "Bronto Cosmos",
    category: "Space Discovery & Astronomy",
    shortDescription:
      "A science, astronomy, and space discovery platform bringing real cosmic data into a single, beautifully designed discovery experience.",
    description:
      "Bronto Cosmos is a science, astronomy, and space discovery platform that brings together open data from leading space agencies. Drawing on daily astronomy imagery, planetary mission photographs, Earth observation feeds, the near-Earth object stream, and a deep visual archive, the platform presents the cosmos as a single, coherent discovery experience designed for reading, browsing, and exploring.",
    capabilities: [
      "Picture of the Day",
      "Mars Rover Photographs",
      "Earth From Space",
      "Asteroid Tracker",
      "Space Gallery News",
      "Planet Facts",
    ],
    appStoreUrl: "https://brontocosmos.com",
    buttonText: "Visit Bronto Cosmos",
    gradient: "from-cyan-400 via-indigo-500 to-violet-600",
    accentColor: "#6366f1",
  },
  {
    id: "mspconnect",
    name: "MSPConnect",
    category: "Agricultural Intelligence",
    shortDescription:
      "An agricultural intelligence platform improving pricing transparency and market access for farmers and stakeholders.",
    description:
      "MSPConnect is an agricultural intelligence and market-awareness platform focused on improving transparency, pricing awareness, and access to information for farmers, traders, and agricultural stakeholders. The platform aims to bridge information gaps between agricultural policy, market conditions, and real-world pricing outcomes through technology-enabled solutions.",
    capabilities: [
      "Pricing Transparency",
      "Market Awareness",
      "Policy Insights",
      "Agricultural Data",
      "Stakeholder Access",
      "Information Bridge",
    ],
    appStoreUrl: "https://apps.apple.com/app/id6755710564",
    buttonText: "Download MSPConnect on the App Store",
    gradient: "from-green-500 via-yellow-500 to-orange-500",
    accentColor: "#22c55e",
  },
];

export const LEADERSHIP = [
  {
    id: "jeet-santosh-bhaskar",
    name: "Jeet Santosh Bhaskar",
    title: "Founder",
    photoUrl:
      "https://customer-assets-jai6qajn.emergentagent.net/job_fa4320f1-9481-4dc5-871c-cc826bab00cf/artifacts/l0smod3f_Jeet%20Santosh%20Bhaskar.jpg",
    shortBio:
      "Jeet Santosh Bhaskar is the founder of Bronto Intelligence Private Limited and an Indian researcher whose work spans technology, artificial intelligence, theoretical physics, economics, mathematics, and quantum computing. His work explores fundamental questions across science, technology, and the mathematical structures that shape our understanding of reality.",
    fullBiography: [
      "Jeet Santosh Bhaskar is an Indian researcher and technology founder whose work spans theoretical physics, artificial intelligence, economics, and mathematics. His work reflects an interdisciplinary approach to understanding complex systems through scientific research and technology.",
      "He is the founder of Bronto Intelligence Private Limited and has developed technology projects including BrontoAI, a human-centered artificial intelligence platform; MSPConnect, a platform focused on agricultural market awareness and access to information; Bronto Cosmos, a space and astronomy discovery platform; and BrontoY, a social platform designed around sharing moments, opinions, and different perspectives.",
      "Alongside his work in technology, his research spans theoretical physics, economics, artificial intelligence, mathematics, and quantum computing. His independent research explores fundamental questions surrounding quantum mechanics, time, spacetime, and the mathematical structures used to describe physical reality, alongside work examining economic systems, quantitative analysis, and quantum computation.",
      "His broader work lies at the intersection of science, technology, and economics, bringing together research and interdisciplinary study to explore complex systems and fundamental questions across these fields.",
    ],
    responsibilities: [
      "Strategic Direction",
      "Corporate Governance",
      "Product Strategy",
      "Technology Development",
      "Long-term Innovation",
      "Organizational Development",
    ],
  },
  {
    id: "santosh-bhaskar",
    name: "Santosh Chandrakant Bhaskar",
    title: "Independent Director",
    photoUrl:
      "https://customer-assets.emergentagent.com/job_parent-hub-32/artifacts/sx0xe65z_SANTOSH%20BHASKAR%20ORIGINAL.jpg",
    shortBio:
      "Santosh Chandrakant Bhaskar is a senior business executive with extensive experience in operations, supply chain management, procurement, and large-scale business functions. He serves as Non-Executive Director of Bronto Intelligence Private Limited.",
    fullBiography: [
      "Santosh Chandrakant Bhaskar is an Indian business executive with extensive experience across supply chain management, operations, procurement, logistics, and organizational leadership.",
      "Throughout his professional career, he has held leadership and management responsibilities within large-scale corporate environments, contributing to operational excellence, process optimization, and business execution. His professional background includes senior responsibilities within Reliance Retail and related business functions, complemented by management education from Welingkar Institute of Management.",
      "As Non-Executive Director of Bronto Intelligence Private Limited, he provides strategic guidance, governance oversight, operational perspective, and long-term business advisory support. His role contributes to the company's corporate governance framework and sustainable growth strategy.",
      "He also serves as Independent Advisory Director of Bronto Platforms, a subsidiary of Bronto Intelligence Private Limited.",
    ],
    responsibilities: [
      "Governance Oversight",
      "Strategic Guidance",
      "Operational Perspective",
      "Business Advisory",
    ],
  },
];

export const MEDIA_RECOGNITION = [
  {
    id: 16,
    publication: "The Print",
    language: "English",
    type: "Article",
    headline:
      "Jeet Santosh Bhaskar's Bronto Intelligence enters social networking with BrontoY",
    summary:
      "National coverage of Jeet Santosh Bhaskar and Bronto Intelligence expanding into social networking with the launch of BrontoY — a platform for sharing moments, expressing opinions, and discovering different perspectives.",
    url: "https://theprint.in/ani-press-releases/jeet-santosh-bhaskars-bronto-intelligence-enters-social-networking-with-brontoy/2991929/",
    date: "2026",
    latest: true,
  },
  {
    id: 17,
    publication: "The Hindu — Business Line",
    language: "English",
    type: "Article",
    headline:
      "Jeet Santosh Bhaskar's Bronto Intelligence enters social networking with BrontoY",
    summary:
      "Business Line feature on Bronto Intelligence's entry into social networking through BrontoY, the new platform led by founder Jeet Santosh Bhaskar for sharing moments and thoughtful, focused conversations.",
    url: "https://www.thehindubusinessline.com/brandhub/pr-release/jeet-santosh-bhaskars-bronto-intelligence-enters-social-networking-with-brontoy/article71248212.ece",
    date: "2026",
    latest: true,
  },
  {
    id: 18,
    publication: "The Tribune",
    language: "English",
    type: "Article",
    headline:
      "Jeet Santosh Bhaskar's Bronto Intelligence enters social networking with BrontoY",
    summary:
      "The Tribune reports on Jeet Santosh Bhaskar broadening the Bronto ecosystem into social networking with BrontoY, joining BrontoAI, MSPConnect, and Bronto Cosmos.",
    url: "https://www.tribuneindia.com/news/business/jeet-santosh-bhaskars-bronto-intelligence-enters-social-networking-with-brontoy/amp/?utm=relatedarticles",
    date: "2026",
    latest: true,
  },
  {
    id: 14,
    publication: "The Times of India",
    language: "English",
    type: "Article",
    headline:
      "Navi Mumbai teen entrepreneur expands tech portfolio with launch of space science platform",
    summary:
      "National coverage of Jeet Santosh Bhaskar expanding the Bronto Intelligence ecosystem with the launch of Bronto Cosmos — a space, science, and astronomy discovery platform — alongside BrontoAI and MSPConnect.",
    url: "https://timesofindia.indiatimes.com/city/mumbai/navi-mumbai-teen-entrepreneur-expands-tech-portfolio-with-launch-of-space-science-platform/articleshow/131964663.cms",
    date: "2026",
  },
  {
    id: 1,
    publication: "The Times of India",
    language: "English",
    type: "Article",
    headline:
      "Mumbai launches homegrown AI platform in 175 countries, earns spot on Apple Store",
    summary:
      "National coverage of Jeet Santosh Bhaskar and the launch of BrontoAI on the Apple App Store, available across 175 countries, as part of India's growing artificial intelligence ecosystem.",
    url: "https://timesofindia.indiatimes.com/city/mumbai/amid-indias-ai-push-mumbai-teen-launches-homegrown-platform-in-175-countries-earns-spot-on-apple-store/articleshow/128908661.cms",
    date: "2026",
  },
  {
    id: 2,
    publication: "The Economic Times",
    language: "English",
    type: "Article",
    headline: "Global AI platform built from Kharghar",
    summary:
      "Feature on Jeet Santosh Bhaskar and the development of BrontoAI by Bronto Intelligence from Kharghar, Navi Mumbai, exploring the platform's global reach and technological foundation.",
    url: "https://enterpriseai.economictimes.indiatimes.com/amp/news/industry/teen-builds-global-ai-platform-from-kharghar-home/128945533",
    date: "2026",
  },
  {
    id: 3,
    publication: "Free Press Journal",
    language: "English",
    type: "Article",
    headline:
      "Navi Mumbai BrontoAI platform goes live in 175 countries",
    summary:
      "In-depth profile on Jeet Santosh Bhaskar and the development of BrontoAI by Bronto Intelligence and its launch across 175 countries on the Apple App Store.",
    url: "https://www.freepressjournal.in/mumbai/navi-mumbai-teen-prodigy-builds-brontoai-platform-without-formal-coding-education-goes-live-in-175-countries",
    date: "2026",
  },
  {
    id: 4,
    publication: "Loksatta — Indian Express",
    language: "Marathi",
    type: "Article",
    headline:
      "Jeet Bhaskar develops Bronto AI emotional intelligence platform",
    summary:
      "Regional language coverage of Jeet Santosh Bhaskar and the development of Bronto AI, focusing on the emotional intelligence capabilities of the platform.",
    url: "https://www.loksatta.com/thane/thane-student-jeet-bhaskar-develops-bronto-ai-emotional-intelligence-app-kharghar-navi-mumbai-rds-00-5786316/",
    date: "2026",
  },
  {
    id: 5,
    publication: "Dailyhunt — Mumbai Live",
    language: "English",
    type: "Article",
    headline:
      "Navi Mumbai develops emotion-sensing AI app Bronto AI",
    summary:
      "Coverage of Jeet Santosh Bhaskar and the development of BrontoAI, an emotion-sensing artificial intelligence platform built by Bronto Intelligence in Navi Mumbai.",
    url: "https://m.dailyhunt.in/news/india/english/mumbai+live+english-epaper-mliveng/navi+mumbai+teen+develops+emotionsensing+ai+app+bronto+ai-newsid-n705660728",
    date: "2026",
  },
  {
    id: 15,
    publication: "Dailyhunt",
    language: "English",
    type: "Article",
    headline:
      "Jeet Santosh Bhaskar expands Bronto Intelligence ecosystem from BrontoAI and MSPConnect to Bronto Cosmos",
    summary:
      "Feature on Jeet Santosh Bhaskar broadening the Bronto Intelligence ecosystem from BrontoAI and MSPConnect into space discovery with the introduction of Bronto Cosmos.",
    url: "https://m.dailyhunt.in/news/india/english/audrey-epaper-dh389e517a46394928bd1e6776041283b4/jeet+santosh+bhaskar+expands+bronto+intelligence+ecosystem+from+brontoai+and+mspconnect+to+bronto+cosmos-newsid-dh389e517a46394928bd1e6776041283b4_36ec1dd06a1411f19bf7fc740098e01e",
    date: "2026",
  },
  {
    id: 6,
    publication: "ABP Majha",
    language: "Marathi",
    type: "Television Interview",
    headline:
      "ABP Majha News Interview with Jeet Santosh Bhaskar",
    summary:
      "Television interview with Jeet Santosh Bhaskar on ABP Majha discussing the vision behind Bronto Intelligence, the development of BrontoAI, and the company's contribution to India's artificial intelligence ecosystem.",
    url: "https://youtu.be/B-WB-WNQUGo",
    date: "2026",
  },
];

export const TECHNOLOGY_FOCUS = [
  {
    title: "Artificial Intelligence",
    description:
      "Building consumer-facing AI platforms focused on natural interaction, multimodal experiences, and accessible intelligence.",
  },
  {
    title: "Digital Platforms",
    description:
      "Developing and operating digital platforms that bridge information gaps and improve access to technology-enabled services.",
  },
  {
    title: "Economic Intelligence",
    description:
      "Creating systems that improve transparency and decision-making across markets, pricing, and economic information.",
  },
  {
    title: "Agricultural Technology",
    description:
      "Building technology-enabled solutions for agricultural stakeholders, addressing information gaps in policy and markets.",
  },
  {
    title: "Public-Impact Technology",
    description:
      "Developing systems that improve how people interact with technology, information, and essential services.",
  },
  {
    title: "Future Infrastructure",
    description:
      "Investing in long-term digital infrastructure that supports a sustainable and inclusive technology ecosystem.",
  },
];

export const FOOTER_LINKS = {
  company: [
    { name: "About", href: "/about" },
    { name: "Leadership", href: "/leadership" },
    { name: "Media & Recognition", href: "/media" },
    { name: "Careers", href: "/careers" },
  ],
  platforms: [
    { name: "BrontoAI", href: "/brontoai" },
    { name: "BrontoY", href: "/brontoy" },
    { name: "Bronto Cosmos", href: "/brontocosmos" },
    { name: "MSPConnect", href: "/mspconnect" },
    { name: "All Platforms", href: "/platforms" },
  ],
  legal: [
    { name: "Privacy Policy", href: "/privacy" },
    { name: "Terms of Use", href: "/terms" },
    { name: "Contact", href: "/contact" },
  ],
};
